import { InventoryHoldArea } from "../../constants"

import type { SelectableAdjustmentType } from "./types"

import {
  InventoryAdjustmentReason,
  InventoryAdjustmentType,
  WarehousePalletDestinationArea,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface AdjustmentTypeMetadata {
  label: string
  description: string
}

export const ADJUSTMENT_TYPE_TO_LABEL: Record<InventoryAdjustmentType, string> = {
  [InventoryAdjustmentType.AdjustQuantity]: "Adjust Quantity",
  [InventoryAdjustmentType.SplitPallets]: "Split Pallet(s)",
  [InventoryAdjustmentType.CombinePallets]: "Combine Pallets",
  [InventoryAdjustmentType.LotBatchCorrection]: "Lot/Batch Correction",
  [InventoryAdjustmentType.AddNewPallets]: "Add New Pallet(s)",
  [InventoryAdjustmentType.As400Adjustment]: "AS400 Adjustment",
} as const

export const ADJUSTMENT_REASON_TO_LABEL: Record<InventoryAdjustmentReason, string> = {
  [InventoryAdjustmentReason.Damage]: "Damage (Material can't be sold)",
  [InventoryAdjustmentReason.Samples]: "Samples (Sent to sample room)",
  [InventoryAdjustmentReason.TollMixing]: "Toll Mixing (Sent to manufacturer)",
  [InventoryAdjustmentReason.LostMaterial]: "Lost Material (Missing / can't locate)",
  [InventoryAdjustmentReason.FoundMaterial]: "Found Material (Not previously in system)",
  [InventoryAdjustmentReason.CustomerReturn]: "Customer Return (Initiatives receiving)",
  [InventoryAdjustmentReason.ExpiredMaterial]: "Expired Material (Shelf life exceeded)",
  [InventoryAdjustmentReason.Other]: "Other",
} as const

export const ADJUSTMENT_TYPE_METADATA: Record<SelectableAdjustmentType, AdjustmentTypeMetadata> = {
  [InventoryAdjustmentType.AdjustQuantity]: {
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.AdjustQuantity],
    description: "Select one or more pallets to adjust quantities.",
  },
  [InventoryAdjustmentType.SplitPallets]: {
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.SplitPallets],
    description:
      "Select source lot(s), enter quantity to split, and choose a destination. Remaining quantity stays under the original reference ID; a new reference ID is auto-assigned.",
  },
  [InventoryAdjustmentType.CombinePallets]: {
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.CombinePallets],
    description: "Select one or more source pallet(s) and choose the target pallet reference ID to combine into.",
  },
  [InventoryAdjustmentType.LotBatchCorrection]: {
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.LotBatchCorrection],
    description: "Select one pallet to correct lot, batch, quantity information, or add a new lot.",
  },
  [InventoryAdjustmentType.AddNewPallets]: {
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.AddNewPallets],
    description:
      "Add a new pallet to the warehouse by specifying the target reference, lot details, and quantities. A new reference ID will be assigned automatically.",
  },
}

export const ADJUSTMENT_TYPE_OPTIONS: Option<string>[] = [
  {
    value: InventoryAdjustmentType.AdjustQuantity,
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.AdjustQuantity],
  },
  {
    value: InventoryAdjustmentType.SplitPallets,
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.SplitPallets],
  },
  {
    value: InventoryAdjustmentType.CombinePallets,
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.CombinePallets],
  },
  {
    value: InventoryAdjustmentType.LotBatchCorrection,
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.LotBatchCorrection],
  },
  {
    value: InventoryAdjustmentType.AddNewPallets,
    label: ADJUSTMENT_TYPE_TO_LABEL[InventoryAdjustmentType.AddNewPallets],
  },
]

export const HOLD_AREA_TO_DESTINATION_AREA: Partial<Record<InventoryHoldArea, WarehousePalletDestinationArea>> = {
  [InventoryHoldArea.InboundHold]: WarehousePalletDestinationArea.InboundHold,
  [InventoryHoldArea.OutboundHold]: WarehousePalletDestinationArea.OutboundHold,
  [InventoryHoldArea.OffSpecArea]: WarehousePalletDestinationArea.OffSpec,
}

export { SHELVING_DEFAULT_FORM_VALUES, DESTINATION_DEFAULT_FORM_VALUES } from "../../constants"

export const MAX_PALLETS_COUNT = 20

export const PALLETS_COUNT_OPTIONS: Option<number>[] = Array.from({ length: MAX_PALLETS_COUNT }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}))
